'use client';

import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { Product } from '@/ui/components/pages/products/types/Product';
import { RichTextRenderer } from '@/ui/components/tileRenderers/RichTextRenderer';
import { ProductCard } from '@/ui/components/pages/products/components/product-card/product-card';

// An item can sit in several category sections, but a filtered list shows it once.
function filterProductsByCategoryOrBadge(products: Product[], filter: string): Product[] {
  const seenProductKeys = new Set<string>();
  return products.filter((product) => {
    const isMatchingFilter =
      product.categoryKey === filter || product.badges.some((badge) => badge.key === filter);
    const productKey = `${product.id}-${product.path}`;
    if (!isMatchingFilter || seenProductKeys.has(productKey)) {
      return false;
    }

    seenProductKeys.add(productKey);
    return true;
  });
}

export interface ProductListProps {
  products: Product[];
  filter: string;
  currency: string;
}

export function ProductList({
  products,
  filter: initialFilter,
  currency,
}: Readonly<ProductListProps>) {
  const searchParams = useSearchParams();
  const [filteredProducts, setFilteredProducts] = useState<Product[]>(products);
  const [filter, setFilter] = useState<string>(initialFilter);

  useEffect(() => {
    const queryFilter = searchParams.get('filter') || 'all-products';
    setFilter(queryFilter);
  }, [searchParams]);

  useEffect(() => {
    if (filter === 'all-products') {
      setFilteredProducts(products);
    } else {
      setFilteredProducts(filterProductsByCategoryOrBadge(products, filter));
    }
  }, [filter, products]);

  if (!filteredProducts?.length) {
    return <p>No products available.</p>;
  }

  const groupedProducts = filteredProducts?.reduce(
    (acc: Record<string, Product[]>, product: Product) => {
      if (product) {
        const { category } = product;
        if (!acc[category]) {
          acc[category] = [];
        }
        acc[category].push(product);
      }
      return acc;
    },
    {},
  );

  return (
    <div className="product-list">
      {filter === 'all-products' ? (
        Object.keys(groupedProducts).map((category) => (
          <div key={category} className="category-section mb-8">
            <RichTextRenderer
              blocks={groupedProducts[category][0].categoryTitle}
              className="mb-4 text-xl"
            />
            <div className="grid grid-cols-2 gap-4 md:grid-cols-2 lg:grid-cols-4">
              {groupedProducts[category].map(
                (product: Product, index) =>
                  product !== undefined && (
                    <ProductCard
                      key={`${product.id}-${index}`}
                      product={product}
                      currency={currency}
                    />
                  ),
              )}
            </div>
          </div>
        ))
      ) : (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-2 lg:grid-cols-4">
          {filteredProducts.map(
            (product: Product, index) =>
              product !== undefined && (
                <ProductCard key={`${product.id}-${index}`} product={product} currency={currency} />
              ),
          )}
        </div>
      )}
    </div>
  );
}
